import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  Search, RefreshCw, Eye, Video,
  Building, Clock, AlertCircle
} from 'lucide-react';

const STATUS_COLORS = {
  pending: '#F59E0B', reviewed: '#4F8EF7',
  shortlisted: '#10B981', interview_scheduled: '#7C3AED',
  hired: '#22D3EE', rejected: '#EF4444', withdrawn: '#94A3B8',
};
const STATUS_LABELS = {
  pending: 'Pending', reviewed: 'Reviewed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview',
  hired: 'Hired', rejected: 'Rejected', withdrawn: 'Withdrawn',
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({
    total: 0, pending: 0, reviewed: 0, shortlisted: 0,
    interview_scheduled: 0, hired: 0, rejected: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Step 1: Get all applications — no joins (avoid RLS issues)
      const { data: rawApps, error: appsErr } = await supabase
        .from('job_applications')
        .select('*')
        .order('applied_at', { ascending: false });

      if (appsErr) {
        setError('Failed to load applications: ' + appsErr.message);
        setLoading(false);
        return;
      }

      if (!rawApps || rawApps.length === 0) {
        setApps([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch related data
      const studentIds = [...new Set(rawApps.map(a => a.student_id).filter(Boolean))];
      const jobIds = [...new Set(rawApps.map(a => a.job_id).filter(Boolean))];
      const companyIds = [...new Set(rawApps.map(a => a.company_id).filter(Boolean))];

      let profileMap = {};
      if (studentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('student_career_profiles')
          .select('user_id, full_name, degree, university_name, cgpa, city, country, phone, resume_url, linkedin_url')
          .in('user_id', studentIds);
        profileMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p]));
      }

      let jobMap = {};
      if (jobIds.length > 0) {
        const { data: jobs } = await supabase
          .from('job_listings')
          .select('id, title, category, job_type')
          .in('id', jobIds);
        jobMap = Object.fromEntries((jobs || []).map(j => [j.id, j]));
      }

      let companyMap = {};
      if (companyIds.length > 0) {
        const { data: companies } = await supabase
          .from('companies')
          .select('id, name, city, country')
          .in('id', companyIds);
        companyMap = Object.fromEntries((companies || []).map(c => [c.id, c]));
      }

      const enriched = rawApps.map(a => ({
        ...a,
        profile: profileMap[a.student_id] || null,
        job: jobMap[a.job_id] || null,
        company: companyMap[a.company_id] || null,
      }));

      setApps(enriched);

      const s = { total: enriched.length };
      Object.keys(STATUS_LABELS).forEach(k => {
        s[k] = enriched.filter(a => a.status === k).length;
      });
      setStats(s);
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = apps.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        a.profile?.full_name?.toLowerCase().includes(q) ||
        a.job?.title?.toLowerCase().includes(q) ||
        a.company?.name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <AdminLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
          All Applications
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
          Platform-wide job applications, statuses and interview details
        </p>
      </div>

      {error && (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: 12, padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 13, color: '#9F1239',
        }}>
          <AlertCircle size={16} color="#EF4444" />
          {error}
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7,1fr)',
        gap: 10, marginBottom: 24,
      }}>
        {[
          { k: 'total', l: 'Total', i: '📋', c: '#4F8EF7' },
          { k: 'pending', l: 'Pending', i: '⏳', c: '#F59E0B' },
          { k: 'reviewed', l: 'Reviewed', i: '👁️', c: '#4F8EF7' },
          { k: 'shortlisted', l: 'Shortlisted', i: '🏆', c: '#10B981' },
          { k: 'interview_scheduled', l: 'Interview', i: '📅', c: '#7C3AED' },
          { k: 'hired', l: 'Hired', i: '🎉', c: '#22D3EE' },
          { k: 'rejected', l: 'Rejected', i: '❌', c: '#EF4444' },
        ].map(({ k, l, i, c }) => (
          <div key={k} style={{
            background: '#fff', borderRadius: 12, padding: '12px 10px',
            border: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', gap: 8,
            cursor: k !== 'total' ? 'pointer' : 'default',
            outline: statusFilter === k ? `2px solid ${c}` : 'none',
          }}
            onClick={() => k !== 'total' &&
              setStatusFilter(statusFilter === k ? 'all' : k)}>
            <span style={{ fontSize: 18 }}>{i}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c }}>
                {stats[k] ?? 0}
              </div>
              <div style={{ fontSize: 9, color: '#94A3B8' }}>{l}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 14,
        border: '1px solid #E2E8F0', padding: '12px 16px',
        marginBottom: 20,
        display: 'flex', gap: 10, alignItems: 'center',
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{
            position: 'absolute', left: 10,
            top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
          }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search student name, job, or company..."
            style={{
              width: '100%', padding: '8px 12px 8px 32px',
              border: '1.5px solid #E2E8F0', borderRadius: 10,
              fontSize: 13, outline: 'none',
            }} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', border: '1.5px solid #E2E8F0',
            borderRadius: 10, fontSize: 13, background: '#fff', outline: 'none',
          }}>
          <option value="all">All Status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <button onClick={load} style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '8px 14px', borderRadius: 10,
          border: '1.5px solid #E2E8F0', background: '#fff',
          fontSize: 13, cursor: 'pointer', color: '#64748B',
        }}>
          <RefreshCw size={13} />Refresh
        </button>
        <span style={{ fontSize: 13, color: '#94A3B8', whiteSpace: 'nowrap' }}>
          {filtered.length} applications
        </span>
      </div>

      {/* Table + Detail */}
      <div style={{ display: 'flex', gap: 20 }}>
        {/* Table */}
        <div style={{
          flex: 1, background: '#fff', borderRadius: 14,
          border: '1px solid #E2E8F0', overflow: 'hidden', minWidth: 0,
        }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
              Loading applications...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 600, color: '#64748B', marginBottom: 4 }}>
                No applications found
              </div>
              <div style={{ fontSize: 13 }}>
                {apps.length === 0
                  ? 'No applications in the platform yet.'
                  : 'No applications match this filter.'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse', minWidth: 700,
              }}>
                <thead>
                  <tr style={{ background: '#F8FAFF' }}>
                    {['Student', 'Job / Company', 'Interview', 'Applied', 'Status', ''].map(h => (
                      <th key={h} style={{
                        padding: '11px 14px', textAlign: 'left',
                        fontSize: 10, fontWeight: 700,
                        color: '#94A3B8', letterSpacing: 0.5,
                        borderBottom: '1px solid #F1F5F9',
                      }}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(a => {
                    const sc = STATUS_COLORS[a.status] || '#94A3B8';
                    const isSelected = selected?.id === a.id;
                    return (
                      <tr key={a.id}
                        style={{
                          borderBottom: '1px solid #F1F5F9',
                          background: isSelected
                            ? 'rgba(79,142,247,0.04)' : 'transparent',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => {
                          if (!isSelected)
                            e.currentTarget.style.background = '#F8FAFF';
                        }}
                        onMouseLeave={e => {
                          if (!isSelected)
                            e.currentTarget.style.background = 'transparent';
                        }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                          }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: 800, fontSize: 12,
                            }}>
                              {(a.profile?.full_name || 'U')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{
                                fontWeight: 700, fontSize: 12, color: '#1E293B',
                              }}>
                                {a.profile?.full_name || `Student (${a.student_id?.slice(0,8)})`}
                              </div>
                              <div style={{ fontSize: 10, color: '#94A3B8' }}>
                                {a.profile?.degree} · {a.profile?.university_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: '#1E293B' }}>
                            {a.job?.title || '—'}
                          </div>
                          <div style={{ fontSize: 10, color: '#94A3B8' }}>
                            {a.company?.name || '—'}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {a.shortlist_type ? (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                              {a.shortlist_type === 'online'
                                ? <Video size={12} color="#4F8EF7" />
                                : <Building size={12} color="#10B981" />}
                              <span style={{
                                fontSize: 11, fontWeight: 600,
                                color: a.shortlist_type === 'online' ? '#4F8EF7' : '#10B981',
                              }}>
                                {a.shortlist_type === 'online' ? 'Online' : 'Physical'}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#CBD5E1' }}>—</span>
                          )}
                          {a.interview_date && (
                            <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>
                              {new Date(a.interview_date).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: '#64748B' }}>
                            {new Date(a.applied_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '3px 8px',
                            borderRadius: 5, background: `${sc}15`, color: sc,
                          }}>
                            {STATUS_LABELS[a.status] || a.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <button
                            onClick={() => setSelected(
                              selected?.id === a.id ? null : a
                            )}
                            style={{
                              padding: '5px 10px', borderRadius: 6,
                              border: '1px solid #E2E8F0',
                              background: isSelected ? '#EFF6FF' : '#F8FAFF',
                              color: isSelected ? '#4F8EF7' : '#64748B',
                              fontSize: 11, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                            <Eye size={11} />
                            {isSelected ? 'Close' : 'View'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{
            width: 340, flexShrink: 0,
            background: '#fff', borderRadius: 14,
            border: '1px solid #E2E8F0', padding: 20,
            overflowY: 'auto', maxHeight: 'calc(100vh - 300px)',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B' }}>
                Application Detail
              </div>
              <button onClick={() => setSelected(null)} style={{
                border: 'none', background: 'none',
                color: '#94A3B8', cursor: 'pointer', fontSize: 18,
              }}>×</button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 17,
              }}>
                {(selected.profile?.full_name || 'U')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B' }}>
                  {selected.profile?.full_name || `Student (${selected.student_id?.slice(0,8)})`}
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>
                  {selected.profile?.degree} · {selected.profile?.university_name}
                </div>
              </div>
            </div>

            {/* Status */}
            <span style={{
              display: 'inline-block', marginBottom: 14,
              fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 8,
              background: `${STATUS_COLORS[selected.status] || '#94A3B8'}15`,
              color: STATUS_COLORS[selected.status] || '#94A3B8',
            }}>
              {STATUS_LABELS[selected.status] || selected.status}
            </span>

            {/* Fields */}
            {[
              ['Job', selected.job?.title],
              ['Company', selected.company?.name],
              ['CGPA', selected.profile?.cgpa],
              ['Location', [selected.profile?.city, selected.profile?.country]
                .filter(Boolean).join(', ')],
              ['Phone', selected.profile?.phone],
              ['Applied', new Date(selected.applied_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })],
              ['Exp. Salary', selected.expected_salary
                ? `PKR ${selected.expected_salary?.toLocaleString()}` : null],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 12, padding: '5px 0',
                borderBottom: '1px solid #F1F5F9',
              }}>
                <span style={{ color: '#94A3B8' }}>{k}</span>
                <span style={{
                  color: '#1E293B', fontWeight: 600, maxWidth: 180,
                  textAlign: 'right', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{v}</span>
              </div>
            ))}

            {/* Interview details */}
            {selected.shortlist_type && (
              <div style={{
                marginTop: 14,
                background: selected.shortlist_type === 'online'
                  ? 'rgba(79,142,247,0.06)' : 'rgba(16,185,129,0.06)',
                border: `1px solid ${selected.shortlist_type === 'online'
                  ? 'rgba(79,142,247,0.2)' : 'rgba(16,185,129,0.2)'}`,
                borderRadius: 10, padding: 12,
              }}>
                <div style={{
                  fontWeight: 700, fontSize: 12, marginBottom: 8,
                  color: selected.shortlist_type === 'online' ? '#4F8EF7' : '#10B981',
                }}>
                  {selected.shortlist_type === 'online'
                    ? '🎥 Online Interview'
                    : '🏢 Physical Interview'}
                </div>
                {selected.interview_date && (
                  <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>
                    📅 {new Date(selected.interview_date).toLocaleString()}
                  </div>
                )}
                {selected.interview_link && (
                  <div style={{ fontSize: 11, marginBottom: 4 }}>
                    🔗{' '}
                    <a href={selected.interview_link} target="_blank" rel="noreferrer"
                      style={{ color: '#4F8EF7' }}>
                      Meeting Link
                    </a>
                  </div>
                )}
                {selected.interview_venue && (
                  <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>
                    📍 {selected.interview_venue}
                  </div>
                )}
                {selected.shortlist_message && (
                  <div style={{
                    fontSize: 11, color: '#64748B',
                    fontStyle: 'italic', marginTop: 4,
                  }}>
                    💬 {selected.shortlist_message}
                  </div>
                )}
              </div>
            )}

            {/* Rejection reason */}
            {selected.rejection_reason && (
              <div style={{
                marginTop: 14, background: '#FFF1F2',
                border: '1px solid #FECDD3', borderRadius: 10, padding: 12,
              }}>
                <div style={{
                  fontWeight: 700, fontSize: 11, color: '#E11D48', marginBottom: 4,
                }}>
                  REJECTION REASON
                </div>
                <div style={{ fontSize: 12, color: '#9F1239' }}>
                  {selected.rejection_reason}
                </div>
              </div>
            )}

            {/* Resume */}
            {(selected.profile?.resume_url || selected.resume_url) && (
              <a href={selected.profile?.resume_url || selected.resume_url}
                target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  marginTop: 14, padding: '9px 14px',
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  borderRadius: 10, color: '#16A34A',
                  fontSize: 12, fontWeight: 700, textDecoration: 'none',
                }}>
                📄 View CV / Resume
              </a>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}